// src/components/ChatBox/useChatWebSocket.ts
// ★ Custom hook encapsulating WebSocket connection lifecycle for the ChatBox.
// Uses callback refs to avoid stale closures and reconnect loops.

import { useRef, useState, useEffect, useCallback } from 'react';
import CLINIC_CONFIG from '../../../config/clinicConfig';
import type { Message, ConnectionStatus } from './chatTypes';

interface UseChatWebSocketOptions {
    sessionId: string;
    onBotMessage: (message: Message) => void;
    onThinking?: (content: string) => void;
    onChunk?: (content: string) => void;
    onComplete?: (content: string, sessionId?: string) => void;
    onSendError: () => void;
}

interface UseChatWebSocketReturn {
    connectionStatus: ConnectionStatus;
    isLoading: boolean;
    sendMessageViaWebSocket: (messageText: string) => void;
    connectWebSocket: () => void;
    handleStartTyping: () => void;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const generateSessionId = (): string => {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export function useChatWebSocket({
    sessionId,
    onBotMessage,
    onThinking,
    onChunk,
    onComplete,
    onSendError,
}: UseChatWebSocketOptions): UseChatWebSocketReturn {
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [isLoading, setIsLoading] = useState(false);
    const [hasTriggeredConnection, setHasTriggeredConnection] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const pendingMessageRef = useRef<string | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const heartbeatIntervalRef = useRef<number | null>(null);
    const isUnmountedRef = useRef(false);
    const connectionTimeoutRef = useRef<number | null>(null);
    const connectWebSocketRef = useRef<() => void>(() => { });
    const sessionIdRef = useRef(sessionId);

    // Must be set during render (not in an effect) so that React StrictMode's
    // second mount clears the flag before any effects run.
    isUnmountedRef.current = false;

    // ★ Callback refs — always point to the latest callback without causing re-renders
    const onBotMessageRef = useRef(onBotMessage);
    const onThinkingRef = useRef(onThinking);
    const onChunkRef = useRef(onChunk);
    const onCompleteRef = useRef(onComplete);
    const onSendErrorRef = useRef(onSendError);

    // Keep refs up to date
    useEffect(() => { onBotMessageRef.current = onBotMessage; }, [onBotMessage]);
    useEffect(() => { onThinkingRef.current = onThinking; }, [onThinking]);
    useEffect(() => { onChunkRef.current = onChunk; }, [onChunk]);
    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
    useEffect(() => { onSendErrorRef.current = onSendError; }, [onSendError]);
    useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

    const WEBSOCKET_URL = CLINIC_CONFIG.aiAgent;
    const CLINIC_ID = CLINIC_CONFIG.clinicId;

    const clearHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
    };

    const clearConnectTimeout = () => {
        if (connectionTimeoutRef.current !== null) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }
    };

    const scheduleReconnect = useCallback((immediate: boolean = false) => {
        if (isUnmountedRef.current) return;
        if (reconnectTimeoutRef.current !== null) return;
        const attempts = reconnectAttemptsRef.current;
        const delay = immediate ? 0 : Math.min(1000 * Math.pow(2, attempts), 10000);
        reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null;
            reconnectAttemptsRef.current = Math.min(attempts + 1, 10);
            try { connectWebSocketRef.current(); } catch { }
        }, delay);
    }, []);

    // ── Send a message on the open socket ────────────────────────────
    const sendMessageNow = useCallback((ws: WebSocket, messageText: string) => {
        const messageData = {
            action: 'message',
            message: messageText,
            sessionId: sessionIdRef.current,
            clinicId: CLINIC_ID,
        };
        ws.send(JSON.stringify(messageData));
        console.log('WS message sent:', messageData);
    }, [CLINIC_ID]);

    // ── Public: send or queue message ────────────────────────────────
    const sendMessageViaWebSocket = useCallback((messageText: string) => {
        try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                sendMessageNow(wsRef.current, messageText);
            } else {
                console.log('WS not open — queuing message as pending');
                pendingMessageRef.current = messageText;
                connectWebSocketRef.current();
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setIsLoading(false);
            onSendErrorRef.current();
        }
    }, [sendMessageNow]);

    // ── Connect WebSocket ────────────────────────────────────────────
    const connectWebSocket = useCallback(() => {
        if (isUnmountedRef.current) return;
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
            return;
        }

        console.log('Connecting WebSocket to:', WEBSOCKET_URL);
        setConnectionStatus('connecting');

        try {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            clearConnectTimeout();

            const ws = new WebSocket(WEBSOCKET_URL);
            wsRef.current = ws;

            // Fail-fast timeout
            connectionTimeoutRef.current = window.setTimeout(() => {
                if (wsRef.current === ws && ws.readyState !== WebSocket.OPEN) {
                    try { ws.close(4000, 'Connect timeout'); } catch { }
                }
            }, 12000);

            ws.onopen = () => {
                if (isUnmountedRef.current) return;
                console.log('WebSocket connected');
                setConnectionStatus('connected');
                reconnectAttemptsRef.current = 0;
                clearHeartbeat();
                clearConnectTimeout();

                // Send any pending message
                const pending = pendingMessageRef.current;
                if (pending) {
                    pendingMessageRef.current = null;
                    try {
                        if (ws.readyState === WebSocket.OPEN) {
                            sendMessageNow(ws, pending);
                        }
                    } catch (err) {
                        console.error('Error sending pending message:', err);
                        onSendErrorRef.current();
                    }
                }
            };

            // ── Message handler — reads callbacks from refs ─────────
            ws.onmessage = (event) => {
                if (isUnmountedRef.current) return;
                try {
                    const data = JSON.parse(event.data);
                    console.log('WS received:', data.type || 'legacy', data);

                    // ── Structured streaming protocol ──
                    if (data.type === 'thinking') {
                        onThinkingRef.current?.(data.content || 'Processing your request...');
                        return;
                    }

                    if (data.type === 'chunk') {
                        onChunkRef.current?.(data.content || '');
                        return;
                    }

                    if (data.type === 'complete') {
                        onCompleteRef.current?.(data.content || '', data.sessionId);
                        setIsLoading(false);
                        return;
                    }

                    // ── Legacy fallback (single-shot response) ──
                    let messageText = '';
                    if (data.message) {
                        messageText = data.message;
                    } else if (data.text) {
                        messageText = data.text;
                    } else if (data.content) {
                        messageText = data.content;
                    } else {
                        messageText = 'I received a response but had trouble understanding it. Could you please try again?';
                    }

                    onBotMessageRef.current({
                        type: 'bot',
                        text: messageText,
                        timestamp: new Date(),
                    });
                } catch (err) {
                    console.error('Error parsing WebSocket message:', err);
                    onBotMessageRef.current({
                        type: 'bot',
                        text: 'I received a response but had trouble understanding it. Could you please try again?',
                        timestamp: new Date(),
                    });
                }
                setIsLoading(false);
            };

            ws.onerror = () => {
                if (isUnmountedRef.current) return;
                console.error('WebSocket error');
                setConnectionStatus('error');
                setIsLoading(false);
                setHasTriggeredConnection(false);
                clearHeartbeat();
                clearConnectTimeout();
                wsRef.current = null;
            };

            ws.onclose = (event) => {
                if (isUnmountedRef.current) return;
                console.log('WebSocket closed:', event.code, event.reason);
                setConnectionStatus('disconnected');
                setIsLoading(false);
                setHasTriggeredConnection(false);
                clearHeartbeat();
                clearConnectTimeout();
                wsRef.current = null;
                if (event.code !== 1000) {
                    scheduleReconnect();
                }
            };
        } catch (err) {
            console.error('Error creating WebSocket:', err);
            setConnectionStatus('error');
            setHasTriggeredConnection(false);
            clearConnectTimeout();
            scheduleReconnect();
        }
    // ★ Only depends on stable values — no callbacks in deps
    }, [WEBSOCKET_URL, scheduleReconnect, sendMessageNow]);

    // Keep imperative ref for scheduler
    useEffect(() => {
        connectWebSocketRef.current = connectWebSocket;
    }, [connectWebSocket]);

    // Auto-connect when sessionId is ready
    useEffect(() => {
        if (sessionId && (connectionStatus === 'disconnected' || connectionStatus === 'error')) {
            setHasTriggeredConnection(true);
            connectWebSocket();
        }
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
            if (wsRef.current) {
                wsRef.current.close(1000, 'Component unmounted');
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            clearConnectTimeout();
            clearHeartbeat();
        };
    }, []);

    // Trigger connection on user typing
    const handleStartTyping = useCallback(() => {
        if (!hasTriggeredConnection && (connectionStatus === 'disconnected' || connectionStatus === 'error')) {
            setHasTriggeredConnection(true);
            connectWebSocket();
        }
    }, [hasTriggeredConnection, connectionStatus, connectWebSocket]);

    return {
        connectionStatus,
        isLoading,
        sendMessageViaWebSocket,
        connectWebSocket,
        handleStartTyping,
        setIsLoading,
    };
}
